import React, { useState, useCallback, useEffect } from 'react';
import { Layers, Wallet, Tablet, Heart, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { features } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const iconMap = {
  Layers,
  Wallet,
  Tablet,
  Heart,
  MessageCircle,
};

const Features = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const { ref: sectionRef, isVisible } = useScrollAnimation();

  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % features.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const goToPrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) goToNext();
    else if (distance < -minSwipeDistance) goToPrev();
  };

  // Auto slide
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    const interval = setInterval(() => {
      goToNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [goToNext]);

  const FeatureCard = ({ feature, className = '' }) => {
    const IconComponent = iconMap[feature.icon];
    return (
      <div className={`group relative bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-2xl p-6 border border-[#C9A97B]/10 hover:border-[#C9A97B]/30 transition-all duration-500 hover-lift h-full ${className}`}>
        {/* Hover Glow */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#C9A97B]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <div className="relative z-10">
          {/* Icon */}
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#C9A97B]/20 to-[#C9A97B]/5 flex items-center justify-center mb-4 group-hover:from-[#C9A97B]/30 group-hover:to-[#C9A97B]/10 transition-all duration-500">
            <IconComponent className="w-7 h-7 text-[#C9A97B]" />
          </div>

          {/* Content */}
          <h3 className="text-xl font-display font-semibold text-[#F5F0E8] mb-1">
            {feature.title}
          </h3>
          <p className="text-sm text-[#C9A97B] mb-3 font-medium">
            {feature.subtitle}
          </p>
          <p className="text-[#BFB5A5] text-sm leading-relaxed">
            {feature.description}
          </p>

          {/* Decorative Line */}
          <div className="mt-6 h-0.5 w-12 bg-gradient-to-r from-[#C9A97B] to-transparent rounded-full group-hover:w-20 transition-all duration-500" />
        </div>
      </div>
    );
  };

  return (
    <section id="features" ref={sectionRef} className="py-16 md:py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0B09] via-[#1A1512] to-[#0D0B09]" />
      
      {/* Decorative */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#C9A97B]/5 rounded-full blur-[150px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-10 md:mb-16 ${isVisible ? 'animate-fadeInDown' : 'opacity-0'}`}>
          <span className={`inline-block px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 ${isVisible ? 'animate-bounceIn' : 'opacity-0'}`} style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            Öne Çıkan Özellikler
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Kuyumcu ve Toptancıya <span className="text-gold-gradient">Tam Ayar</span> Çözümler
          </h2>
          <p className="text-[#BFB5A5] text-base md:text-lg max-w-2xl mx-auto">
            Her bir özelliğimiz, sektörün gerçek ihtiyaçlarından doğdu. 
            Basit bir stok programı değil, işinizin dijital beyni.
          </p>
        </div>

        {/* Desktop Features Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            // Sol sütun: soldan, orta sütun: alttan, sağ sütun: sağdan
            const colPosition = index % 3;
            const animationClass = colPosition === 0 
              ? 'animate-slideInLeft' 
              : colPosition === 1 
                ? 'animate-slideInUp' 
                : 'animate-slideInRight';
            
            return (
              <div 
                key={feature.id} 
                className={isVisible ? animationClass : 'opacity-0'}
                style={{ animationDelay: `${index * 120 + 300}ms`, animationFillMode: 'forwards' }}
              >
                <FeatureCard feature={feature} />
              </div>
            );
          })}
        </div>

        {/* Mobile Features Slider */}
        <div className="md:hidden">
          <div 
            className="relative overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div 
              className="flex transition-transform duration-400 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {features.map((feature) => (
                <div key={feature.id} className="w-full flex-shrink-0 px-1">
                  <FeatureCard feature={feature} />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={goToPrev}
              disabled={isAnimating}
              className="w-10 h-10 rounded-full bg-[#1F1914] border border-[#C9A97B]/20 flex items-center justify-center text-[#C9A97B] hover:bg-[#C9A97B]/10 transition-colors disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-1.5">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isAnimating) {
                      setIsAnimating(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsAnimating(false), 400);
                    }
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-6 bg-[#C9A97B]' 
                      : 'w-2 bg-[#C9A97B]/30 hover:bg-[#C9A97B]/50'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goToNext}
              disabled={isAnimating}
              className="w-10 h-10 rounded-full bg-[#1F1914] border border-[#C9A97B]/20 flex items-center justify-center text-[#C9A97B] hover:bg-[#C9A97B]/10 transition-colors disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
